// ============================================================================
// BOQ — conceptual space-type area-rate corpus (boq.area_rates)
// ============================================================================
// Coarser sibling of useBoqSdRates: all-in rates per sqm of a space type
// (indoor / covered outdoor / ...), keyed to a building typology. Used to prime
// a new SD/concept estimate at order-of-magnitude grain. Raw PostgREST on the
// boq schema; writes restricted to admin/supervisor by RLS.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';

const num = (v) => (v === null || v === undefined ? null : Number(v));

const SELECT = `
  id, space_type, typology_id, rate, currency, region, rate_date,
  project_id, source_zone_id, zone_label, city, source, note, created_at,
  projects:project_id ( project_code, name ),
  typology:typology_id ( id, label )
`.replace(/\s+/g, ' ');

const mapRate = (r) => ({
  id: r.id,
  spaceType: r.space_type,
  typologyId: r.typology_id,
  typologyLabel: r.typology?.label || null,
  rate: num(r.rate),
  currency: r.currency || 'INR',
  region: r.region || 'IN',
  rateDate: r.rate_date,
  projectId: r.project_id,
  projectCode: r.projects?.project_code || null,
  projectName: r.projects?.name || null,
  sourceZoneId: r.source_zone_id,
  zoneLabel: r.zone_label,
  city: r.city,
  source: r.source,
  note: r.note,
  createdAt: r.created_at,
});

export const useBoqAreaRates = ({ enabled = true } = {}) => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fErr } = await supabase
        .schema('boq')
        .from('area_rates')
        .select(SELECT)
        .order('rate_date', { ascending: false });
      if (fErr) throw fErr;
      setRates((data || []).map(mapRate));
    } catch (err) {
      console.error('[useBoqAreaRates] fetch failed', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'area rates' }));
    } finally {
      setLoading(false);
    }
  }, []);

  const createRates = useCallback(async (rows) => {
    if (!rows?.length) return { success: true, count: 0 };
    try {
      const { data: sessionData, error: sErr } = await supabase.auth.getSession();
      if (sErr || !sessionData?.session) return { success: false, error: 'Please sign in.' };
      const payload = rows.map((row) => ({
        space_type: row.spaceType,
        typology_id: row.typologyId || null,
        rate: Number(row.rate),
        currency: row.currency || 'INR',
        region: row.region || 'IN',
        rate_date: row.rateDate || new Date().toISOString().slice(0, 10),
        project_id: row.projectId || null,
        zone_label: row.zoneLabel?.trim() || null,
        city: row.city?.trim() || null,
        source: row.source || 'manual',
        note: row.note?.trim() || null,
      }));
      const { data, error: insErr } = await supabase
        .schema('boq').from('area_rates').insert(payload).select(SELECT);
      if (insErr) throw insErr;
      const mapped = (data || []).map(mapRate);
      setRates((prev) => [...mapped, ...prev]);
      return { success: true, count: mapped.length };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'area rates' }) };
    }
  }, []);

  const deleteMany = useCallback(async (ids) => {
    if (!ids?.length) return { success: true };
    try {
      const { error: dErr } = await supabase.schema('boq').from('area_rates').delete().in('id', ids);
      if (dErr) throw dErr;
      const idSet = new Set(ids);
      setRates((prev) => prev.filter((r) => !idSet.has(r.id)));
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'area rates' }) };
    }
  }, []);

  useEffect(() => { if (enabled) fetchAll(); }, [enabled, fetchAll]);

  return { rates, loading, error, refresh: fetchAll, createRates, deleteMany };
};

export default useBoqAreaRates;
